import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';

const API = process.env.REACT_APP_BACKEND_URL;

async function call(path, body) {
  const res = await fetch(`${API}/api${path}`, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Something went wrong. Please try again.');
  return data;
}

const box = 'max-w-md mx-auto my-16 p-8 bg-slate-900 border border-slate-700 rounded-2xl text-white space-y-5';
const btn = 'w-full py-3 rounded-lg bg-blue-500 hover:bg-blue-600 font-semibold transition-colors disabled:opacity-50';
const inp = 'w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg tracking-widest focus:outline-none focus:border-blue-500';

function BackupCodes({ codes, onDone }) {
  const [saved, setSaved] = useState(false);
  return (
    <div className={box} data-testid="admin-2fa-backup-codes">
      <h2 className="text-xl font-bold">Your backup codes</h2>
      <p className="text-sm text-slate-300">Each code works once if you lose your phone. Print them or keep them somewhere offline. They won't be shown again.</p>
      <ul className="grid grid-cols-2 gap-2 font-mono text-lg" data-testid="admin-2fa-backup-code-list">
        {codes.map((c) => <li key={c} className="bg-slate-800 rounded px-3 py-1 text-center">{c}</li>)}
      </ul>
      <button className="w-full py-2 rounded-lg border border-slate-600 hover:bg-slate-800 text-sm transition-colors" onClick={() => navigator.clipboard?.writeText(codes.join('\n'))} data-testid="admin-2fa-copy-codes">Copy codes</button>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="admin-2fa-codes-saved" />
        I have stored these codes offline
      </label>
      <button className={btn} disabled={!saved} onClick={onDone} data-testid="admin-2fa-finish">Continue to Admin</button>
    </div>
  );
}

function Setup({ token, onEnabled }) {
  const [stage, setStage] = useState('start');
  const [code, setCode] = useState('');
  const [qr, setQr] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setErr(''); setBusy(true); try { await fn(); } catch (e) { setErr(e.message); } setBusy(false); };

  const start = () => run(async () => { await call('/admin/2fa/start', { session_token: token }); setStage('email'); });
  const checkEmail = () => run(async () => { setQr(await call('/admin/2fa/setup', { session_token: token, code })); setCode(''); setStage('scan'); });
  const enable = () => run(async () => { const r = await call('/admin/2fa/enable', { session_token: token, code }); onEnabled(r.backup_codes); });

  return (
    <div className={box} data-testid="admin-2fa-setup">
      <h2 className="text-xl font-bold">Set up two-step sign-in</h2>
      {stage === 'start' && (<>
        <p className="text-sm text-slate-300">Admin now needs a code from an authenticator app (for example Google Authenticator or Microsoft Authenticator) as well as your password. First we'll email you a one-time set-up code.</p>
        <button className={btn} disabled={busy} onClick={start} data-testid="admin-2fa-send-email-code">Email me a set-up code</button>
      </>)}
      {stage === 'email' && (<>
        <p className="text-sm text-slate-300">Enter the 6-digit code we just emailed you. It expires in 10 minutes.</p>
        <input className={inp} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="admin-2fa-email-code-input" />
        <button className={btn} disabled={busy || !code} onClick={checkEmail} data-testid="admin-2fa-email-code-submit">Continue</button>
      </>)}
      {stage === 'scan' && qr && (<>
        <p className="text-sm text-slate-300">Scan this with your authenticator app, then enter the 6-digit code it shows.</p>
        <img src={qr.qr_data_url} alt="Authenticator QR code" className="mx-auto w-48 h-48 bg-white rounded" data-testid="admin-2fa-qr" />
        <p className="text-xs text-slate-400 break-all">Can't scan? Enter this key: <span className="font-mono" data-testid="admin-2fa-manual-key">{qr.manual_key}</span></p>
        <input className={inp} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="admin-2fa-app-code-input" />
        <button className={btn} disabled={busy || !code} onClick={enable} data-testid="admin-2fa-enable">Turn on two-step sign-in</button>
      </>)}
      {err && <p className="text-sm text-red-400" data-testid="admin-2fa-error">{err}</p>}
    </div>
  );
}

export const AdminTwoFactorGate = ({ children }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [status, setStatus] = useState(null);
  const [codes, setCodes] = useState(null);

  useEffect(() => {
    (async () => {
      const t = await apiService.getOrCreateSession();
      setToken(t);
      try { setStatus(await call(`/admin/2fa/status?session_token=${encodeURIComponent(t)}`)); }
      catch { navigate('/login'); }
    })();
  }, [navigate]);

  const page = (el) => (
    <div className="min-h-screen bg-slate-950 px-4 py-1 pb-32">
      {el}
      {!codes && <button className="block mx-auto text-sm text-slate-400 hover:text-white transition-colors" onClick={() => navigate('/dashboard')} data-testid="admin-2fa-back">Back to dashboard</button>}
    </div>
  );
  if (codes) return page(<BackupCodes codes={codes} onDone={() => { setCodes(null); setStatus({ enabled: true }); }} />);
  if (!status) return page(<div className="text-center text-slate-400 mt-16" data-testid="admin-2fa-loading">Checking…</div>);
  if (!status.enabled) return page(<Setup token={token} onEnabled={setCodes} />);
  return children;
};
